import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { SectionHome, LAST_TAB_PREFIX } from '../components/enterprise';
import { ShieldAlert, AlertTriangle, Crosshair, Box } from 'lucide-react';

const getLastTab = (section) => {
  try {
    const raw = localStorage.getItem(`${LAST_TAB_PREFIX}${section}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const RisicosHomePage = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState({ highCritical: 0, vulnOpen: 0, threats: 0, noOwner: 0, topRisk: null });
  const lastTab = getLastTab('risicos');

  useEffect(() => {
    const fetch = async () => {
      try {
        const LEVEL_MAP = { critical: 'kritiek', high: 'hoog', medium: 'midden', low: 'laag', minimal: 'miniem' };
        const [riskRes, vulnRes, threatRes] = await Promise.all([
          api.get('/ggrc/risks/dashboard').catch(() => ({ data: { risks: [] } })),
          api.get('/vulnerabilities').catch(() => ({ data: [] })),
          api.get('/threats').catch(() => ({ data: [] })),
        ]);
        const risks = (riskRes.data?.risks || []).map(r => ({ ...r, risk_level: LEVEL_MAP[r.risk_level] || r.risk_level || 'laag' }));
        const hc = risks.filter(r => r.risk_level === 'kritiek' || r.risk_level === 'hoog').length;
        const noOwner = risks.filter(r => !r.owner_name && !r.owner).length;
        const topRisk = risks.sort((a, b) => {
          const lo = { kritiek: 5, hoog: 4, midden: 3, laag: 2, miniem: 1 };
          return (lo[b.risk_level] || 0) - (lo[a.risk_level] || 0);
        })[0];
        const vulns = Array.isArray(vulnRes.data) ? vulnRes.data : vulnRes.data?.vulnerabilities || [];
        const vulnOpen = vulns.filter(v => v.severity === 'critical' || v.severity === 'high').length;
        const threats = Array.isArray(threatRes.data) ? threatRes.data.length : threatRes.data?.threats?.length || 0;
        setStats({ highCritical: hc, vulnOpen, threats, noOwner, topRisk });
      } catch {}
    };
    fetch();
  }, [api]);

  return (
    <MainLayout>
      <SectionHome
        question="Waar wil je inzicht in of actie op ondernemen?"
        subtitle="Kies wat je vandaag wilt bereiken. Je kunt dit later altijd wijzigen."
        resume={lastTab ? { title: lastTab.label, href: lastTab.href } : undefined}
        recommended={{
          title: 'Beoordeel mijn belangrijkste risico',
          description: stats.topRisk
            ? `"${stats.topRisk.title}" is het meest urgente risico. Beoordeel de impact, wijs een eigenaar toe en kies een oplossing.`
            : 'Beoordeel impact, eigenaar en oplossing van het meest urgente risico.',
          href: '/risicos/overzicht',
          status: stats.highCritical > 0 ? `${stats.highCritical} hoog/kritiek` : null,
          duration: 'Ongeveer 5 minuten per risico',
          icon: ShieldAlert,
        }}
        alternatives={[
          {
            title: 'Prioriteer open kwetsbaarheden',
            description: 'Bekijk welke kwetsbaarheid eerst aandacht nodig heeft.',
            href: '/risicos/kwetsbaarheden',
            status: stats.vulnOpen > 0 ? `${stats.vulnOpen} urgent` : null,
            icon: AlertTriangle,
          },
          {
            title: 'Bekijk relevante dreigingen',
            description: 'Bekijk aanvallen die passen bij jouw situatie.',
            href: '/risicos/dreigingen',
            status: stats.threats > 0 ? `${stats.threats} dreigingen` : null,
            icon: Crosshair,
          },
          {
            title: 'Wijs systeemeigenaren toe',
            description: 'Leg vast welke systemen belangrijk zijn en wie verantwoordelijk is.',
            href: '/risicos/systemen',
            status: stats.noOwner > 0 ? `${stats.noOwner} zonder eigenaar` : null,
            icon: Box,
          },
        ]}
        overviewHref="/risicos/overzicht"
        overviewLabel="Bekijk het volledige risico-overzicht"
        testId="risicos-home"
      />
    </MainLayout>
  );
};

export default RisicosHomePage;
